// One-time friendly push-permission prompt (Jaime-approved, 2026-09-17).
// Shows once per browser (localStorage flag) after login; declining never
// nags again — the user can still enable from Perfil ("Activar notificaciones").

import { useEffect, useState } from 'react';
import { useAuthOptional } from '../contexts/AuthContext';
import {
  enablePushNotifications,
  pushPromptShown,
  markPushPromptShown,
} from '../lib/push';

export default function PushNotificationSetup() {
  // FIX #110 (2026-09-24): this prompt is cosmetic; degrade to logged-out
  // instead of crashing if the tree is transiently outside AuthProvider.
  const { user } = useAuthOptional() ?? { user: null };
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    if (pushPromptShown()) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return; // already decided — do not nag
    setVisible(true);
  }, [user]);

  if (!visible) return null;

  const close = () => { markPushPromptShown(); setVisible(false); };

  const handleAllow = async () => {
    setBusy(true);
    const result = await enablePushNotifications();
    setBusy(false);
    close();
    if (!result.ok) setFeedback('No se pudieron activar las notificaciones en este dispositivo.');
  };

  return (
    <>
      {feedback && (
        <div className="fixed bottom-4 right-4 z-[100] rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 shadow-lg">
          {feedback}
        </div>
      )}
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" onClick={close}>
        <div className="w-full max-w-md rounded-2xl bg-[var(--v-surface-1)] border border-[var(--v-border-light)] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <h2 className="text-lg font-bold text-[var(--v-text-primary)] mb-2">Notificaciones de Vecindus</h2>
          <p className="text-sm text-[var(--v-text-secondary)] mb-6">
            ¿Quieres recibir avisos de visitas y mensajes aunque la app esté cerrada?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={close}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] transition-colors"
            >
              Ahora no
            </button>
            <button
              onClick={handleAllow}
              disabled={busy}
              className="px-4 py-2 rounded-xl text-sm font-bold bg-[var(--v-teal-400)] text-white hover:bg-[var(--v-teal-500)] disabled:opacity-60 transition-colors"
            >
              {busy ? 'Activando…' : 'Permitir'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Small reusable entry for Perfil/Configuración: lets the user enable push
// (or re-subscribe) at any time. Shows current permission state.
export function PushEnableCard() {
  const [state, setState] = useState<string>('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof Notification !== 'undefined') setState(Notification.permission);
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    const result = await enablePushNotifications();
    setBusy(false);
    if (typeof Notification !== 'undefined') setState(Notification.permission);
    if (result.ok) {
      setMsg('Notificaciones activadas ✔');
    } else if (result.reason === 'denied') {
      setMsg('Permiso denegado. Actívalo desde la configuración del navegador.');
    } else if (result.reason === 'unsupported') {
      setMsg('Este navegador no soporta notificaciones push.');
    } else {
      setMsg('No se pudieron activar las notificaciones.');
    }
  };

  const label = state === 'granted' ? 'Notificaciones activadas' : 'Activar notificaciones';

  return (
    <div className="mb-6 rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-4 flex items-center justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-[var(--v-text-primary)]">Notificaciones push</p>
        <p className="text-xs text-[var(--v-text-secondary)]">Avisos de visitas y mensajes aunque la app esté cerrada.</p>
        {msg && <p className="text-xs mt-1 text-[var(--v-teal-500)]">{msg}</p>}
      </div>
      <button
        onClick={handleEnable}
        disabled={busy || state === 'granted'}
        className="px-4 py-2 rounded-xl text-sm font-bold bg-[var(--v-teal-400)] text-white hover:bg-[var(--v-teal-500)] disabled:opacity-60 whitespace-nowrap transition-colors"
      >
        {busy ? 'Activando…' : label}
      </button>
    </div>
  );
}
